import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import type { RunDetailResponse } from "@/api/types";
import {
    type ChapterIntroDef,
} from "@/components/ChapterIntroBanner";
import {
    getScenarioUpcomingIntel,
    getScenarioCurrentIntel,
    isHighImpactIntel,
} from "@/content/scenarioPresentation";
import { getScenarioChapters } from "@/content/scenarioChapters";

/**
 * WeeklyTurn 최상단에 박히는 한 줄 서사 배너.
 *
 * <p>플레이어 피드백 "지금 어디 있는지 모르겠다" 에 대응해 현재 장·주차·다음
 * 예고된 장면까지 남은 주를 <strong>서사 톤</strong> 한 문장으로 묶어 시선
 * 최우선에 노출한다.
 *
 * <p>예: "행군 12주차. 다음 예고된 장면 \"벼랑끝 전투\" 까지 20주가 남았다. · 병참의 자리에서."
 *
 * <p>2026-04-22: 포맷 결정 — (A) 시스템 톤 vs (B) 서사 톤 중 <strong>B</strong>
 * 채택. 판타지 장부의 무게를 살리되 핵심 수치(주차/남은 주)는 문장 안에 섞어
 * 정보 밀도도 지킨다. 기존 chip/next-goal 패널은 <strong>제거</strong> — HUD 와
 * 중복 표기가 되어 오히려 시선 산만.
 */
interface SituationBannerProps {
    run: RunDetailResponse;
    /** "내 역할" 라벨 (전열/병참/정찰/미정 등). "미정" 이면 문장에 안 들어감. */
    roleLabel?: string | null;
    /** 시나리오 팩별 장 정의. 미제공 시 frontier-vanguard 기본값. */
    chapters?: ChapterIntroDef[];
    recentContextTitle?: string | null;
    recentContextBody?: string | null;
}

export function SituationBanner({
    run,
    roleLabel,
    chapters,
    recentContextTitle,
    recentContextBody,
}: SituationBannerProps) {
    void recentContextTitle;
    void recentContextBody;
    const chapterList = chapters ?? getScenarioChapters(run.scenario.id);
    const currentChapter = chapterList.find(
        c => run.currentWeek >= c.startWeek && run.currentWeek <= c.endWeek,
    );
    // 2026-04-23 E24: "critical week" 분기.
    //   이번 주차 자체가 major intel 엔트리(12, 21, 32, 46, 49, 56, 73, 80, 88) 면
    //   "다음 예고 N주 뒤" 가 아니라 <strong>이번 주의 사건</strong> 을 banner 에 박는다.
    //   critical 이면 banner 스타일도 ember 로 격상해 "평소와 다른 주" 감각 전달.
    //   2026-04-24: scenarioPresentation 범용화 이후 시나리오 id 분기 불필요 —
    //   getScenarioCurrentIntel 자체가 해당 팩에 intel 이 없으면 null 반환.
    const currentIntel = getScenarioCurrentIntel(run.scenario.id, run.currentWeek);
    const upcoming = !currentIntel
        ? getScenarioUpcomingIntel(run.scenario.id, run.currentWeek)
        : null;
    const isCritical = Boolean(currentIntel);
    const isHighImpact = currentIntel ? isHighImpactIntel(currentIntel.kind) : false;

    const sentence = composeSentence({
        chapterTitle: currentChapter?.title ?? null,
        currentWeek: run.currentWeek,
        chapterStart: currentChapter?.startWeek ?? 1,
        maxWeeks: run.maxWeeks,
        currentIntelTitle: currentIntel?.title ?? null,
        currentIntelKind: currentIntel?.kind ?? null,
        upcomingInWeeks: upcoming ? upcoming.atWeek - run.currentWeek : null,
        upcomingTitle: upcoming?.title ?? null,
        roleLabel: roleLabel && roleLabel !== "미정" ? roleLabel : null,
    });

    const accent = isHighImpact
        ? tokens.color.ember
        : isCritical
            ? tokens.color.brass
            : tokens.color.brass;
    const bannerBorder = isCritical ? `${accent}66` : `${tokens.color.brass}33`;
    const bannerShadow = isCritical
        ? `0 0 22px ${accent}44, inset 0 1px 0 ${accent}22`
        : `0 0 18px ${tokens.color.ink}88, inset 0 1px 0 ${tokens.color.brass}18`;

    return (
        <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: tokens.easing.decelerate }}
            className="text-center"
            style={{ width: "100%" }}
            role="status"
            aria-live="polite"
        >
            <div
                className="mx-auto rounded-pill px-l py-[6px] flex items-center justify-center gap-s"
                style={{
                    width: "min(920px, 100%)",
                    background: isCritical
                        ? `linear-gradient(180deg, ${tokens.color.ink}E0, ${accent}22)`
                        : `linear-gradient(180deg, ${tokens.color.ink}D0, ${tokens.color.ink}A8)`,
                    border: `1px solid ${bannerBorder}`,
                    boxShadow: bannerShadow,
                    backdropFilter: "blur(4px)",
                }}
            >
                {isCritical && (
                    <motion.span
                        aria-hidden
                        className="shrink-0 font-display"
                        animate={isHighImpact
                            ? { opacity: [0.6, 1, 0.6], textShadow: [`0 0 8px ${accent}55`, `0 0 18px ${accent}AA`, `0 0 8px ${accent}55`] }
                            : { opacity: 1 }
                        }
                        transition={{ duration: 2, repeat: isHighImpact ? Infinity : 0, ease: "easeInOut" }}
                        style={{
                            color: accent,
                            fontSize: tokens.type.size.body,
                            letterSpacing: "0.1em",
                        }}
                    >
                        {glyphForIntelKind(currentIntel?.kind ?? "")}
                    </motion.span>
                )}
                <p
                    className="font-body"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        lineHeight: 1.55,
                        textShadow: `0 1px 4px ${tokens.color.ink}`,
                    }}
                >
                    {sentence}
                </p>
            </div>
        </motion.div>
    );
}

function composeSentence(args: {
    chapterTitle: string | null;
    currentWeek: number;
    chapterStart: number;
    maxWeeks: number;
    /** 이번 주차 자체가 major intel 일 때 제목. */
    currentIntelTitle: string | null;
    /** 이번 주차 intel 의 kind ("주요 교전", "장 전환" 등). 문장 톤 결정에 사용. */
    currentIntelKind: string | null;
    upcomingInWeeks: number | null;
    upcomingTitle: string | null;
    roleLabel: string | null;
}): string {
    const chapterWeek = Math.max(1, args.currentWeek - args.chapterStart + 1);
    const head = args.chapterTitle
        ? `${stripGiPostfix(args.chapterTitle)} ${chapterWeek}주차`
        : `${args.currentWeek}주차`;

    const tail = (() => {
        // critical week: 이번 주차 자체가 major intel.
        if (args.currentIntelTitle) {
            const kind = args.currentIntelKind ?? "예고된 장면";
            if (kind.includes("결단") || kind.includes("최종")) {
                return `오늘, "${args.currentIntelTitle}" 의 결단이 찾아온다.`;
            }
            if (kind.includes("교전") || kind.includes("전투")) {
                return `오늘, "${args.currentIntelTitle}" 의 칼날이 부딪힌다.`;
            }
            if (kind.includes("분기")) {
                return `오늘, "${args.currentIntelTitle}" 의 갈림길이 열린다.`;
            }
            if (kind.includes("추모") || kind.includes("컷신")) {
                return `오늘, "${args.currentIntelTitle}" 의 밤이 깊어진다.`;
            }
            if (kind.includes("장 전환") || kind.includes("전환")) {
                return `오늘, "${args.currentIntelTitle}" 으로 장이 바뀐다.`;
            }
            // 기본 critical 문장
            return `오늘, "${args.currentIntelTitle}" 의 결이 닿는다.`;
        }
        if (args.upcomingInWeeks == null) {
            return `전체 여정 ${args.maxWeeks}주 중 ${args.currentWeek}주가 지났다.`;
        }
        if (args.upcomingInWeeks <= 0) {
            return args.upcomingTitle
                ? `이번 주 "${args.upcomingTitle}" 의 결이 닿는다.`
                : "이번 주 예고된 장면이 열린다.";
        }
        if (args.upcomingInWeeks === 1) {
            return args.upcomingTitle
                ? `다음 주 "${args.upcomingTitle}" 가 찾아온다.`
                : "다음 주 예고된 장면이 열린다.";
        }
        return args.upcomingTitle
            ? `다음 예고된 장면 "${args.upcomingTitle}" 까지 ${args.upcomingInWeeks}주가 남았다.`
            : `다음 예고된 장면까지 ${args.upcomingInWeeks}주가 남았다.`;
    })();

    const roleBit = args.roleLabel ? ` · ${args.roleLabel}의 자리에서.` : "";
    return `${head}. ${tail}${roleBit}`;
}

/** intel kind 에 맞는 1글자 glyph. banner 앞에 박아 시각 차등. */
function glyphForIntelKind(kind: string): string {
    if (kind.includes("결단") || kind.includes("최종")) return "⚖";
    if (kind.includes("교전") || kind.includes("전투")) return "⚔";
    if (kind.includes("분기")) return "✧";
    if (kind.includes("추모") || kind.includes("컷신")) return "🕯";
    if (kind.includes("전환")) return "📖";
    if (kind.includes("탐사")) return "🗺";
    return "◆";
}

/**
 * "행군기" → "행군", "거점 확보기" → "거점 확보", "원정 준비기" → "원정 준비".
 * 서사 문장에 장 이름이 자연스럽게 들어가도록 어미(-기) 제거.
 */
function stripGiPostfix(title: string): string {
    return title.endsWith("기") ? title.slice(0, -1) : title;
}
